import * as _ from 'lodash';
import * as moment from 'moment';
import { FormEvent, useEffect, useReducer, useState } from 'react';
import { ICenterBase } from '@nkr/models/index';
import { Alert, Button, Col, ControlLabel, Form, FormControl, FormGroup, Modal } from 'react-bootstrap'
import MaskedInput from 'react-maskedinput';
import usePrevious from '@nkr/services/use-previous';
import { Utility } from '@nkr/services/utility';

interface IProps {
    show: boolean,
    centerId: number,
    centers: ICenterBase[],
    showCenter: boolean,
    onSave: (centerId: string, dataDate: string,
        ddTxps: string, ldTxpps: string, wlTotal: string, wlActive: string,
        lda: string, ldb: string, ldab: string, ldo: string,
        ldra: string, ldrb: string, ldrab: string, ldro: string) => void,
    onCancel: () => void
}

interface IState {
    centerId: string,
    dataDate: string,
    ddTxps: string,
    ldTxps: string,
    wlTotal: string,
    wlActive: string,

    lda: string,
    ldb: string,
    ldab: string,
    ldo: string,

    ldra: string,
    ldrb: string,
    ldrab: string,
    ldro: string,
}

const initialState: IState = {
    centerId: '0',
    dataDate: '',
    ddTxps: __ENVIRONMENT__ == 'dev' ? '1' : '',
    ldTxps: __ENVIRONMENT__ == 'dev' ? '2' : '',
    wlTotal: __ENVIRONMENT__ == 'dev' ? '3' : '',
    wlActive: __ENVIRONMENT__ == 'dev' ? '4' : '',

    lda: __ENVIRONMENT__ == 'dev' ? '5' : '',
    ldb: __ENVIRONMENT__ == 'dev' ? '6' : '',
    ldab: __ENVIRONMENT__ == 'dev' ? '7' : '',
    ldo: __ENVIRONMENT__ == 'dev' ? '8' : '',

    ldra: __ENVIRONMENT__ == 'dev' ? '9' : '',
    ldrb: __ENVIRONMENT__ == 'dev' ? '10' : '',
    ldrab: __ENVIRONMENT__ == 'dev' ? '11' : '',
    ldro: __ENVIRONMENT__ == 'dev' ? '12' : '',
};

interface IValidation {
    center: boolean,
    dataDate: boolean,

    ddTxps: boolean,
    ldTxps: boolean,
    wlTotal: boolean,
    wlActive: boolean,

    lda: boolean,
    ldb: boolean,
    ldab: boolean,
    ldo: boolean,

    ldra: boolean,
    ldrb: boolean,
    ldrab: boolean,
    ldro: boolean,
}

const initialValidation: IValidation = {
    center: true,
    dataDate: true,

    ddTxps: true,
    ldTxps: true,
    wlTotal: true,
    wlActive: true,

    lda: true,
    ldb: true,
    ldab: true,
    ldo: true,

    ldra: true,
    ldrb: true,
    ldrab: true,
    ldro: true,
};

const useValidation = () => {
    const [data, setData] = useState<IValidation>({ ...initialValidation });

    const validate = (state: IState) => {
        let newData: IValidation = {
            center: state.centerId != '0',
            dataDate: state.dataDate.indexOf('_') < 0 && moment(state.dataDate, 'MM-YYYY').isValid(),
            ddTxps: Utility.isNumber(state.ddTxps),
            ldTxps: Utility.isNumber(state.ldTxps),
            wlTotal: Utility.isNumber(state.wlTotal),
            wlActive: Utility.isNumber(state.wlActive),

            lda: Utility.isNumber(state.lda),
            ldb: Utility.isNumber(state.ldb),
            ldab: Utility.isNumber(state.ldab),
            ldo: Utility.isNumber(state.ldo),

            ldra: Utility.isNumber(state.ldra),
            ldrb: Utility.isNumber(state.ldrb),
            ldrab: Utility.isNumber(state.ldrab),
            ldro: Utility.isNumber(state.ldro),
        };

        setData(newData);
    };

    const reset = () => {
        setData({ ...initialValidation });
    };

    const isValid = () => {
        let { center, dataDate, ddTxps, ldTxps, wlTotal, wlActive, lda, ldb, ldab, ldo, ldra, ldrb, ldrab, ldro } = data;
        let isValid = center && dataDate && ddTxps && ldTxps && wlTotal && wlActive && lda && ldb && ldab && ldo && ldra && ldrb && ldrab && ldro;

        return isValid;
    }

    return { validation: data, reset, validate, isValid };
}

const reducer = (state: IState, action) => {
    let newState = _.cloneDeep(state);

    switch (action.type) {
        case 'CHANGE-FIELD':
            newState[action.property] = action.value;
            break;

        case 'SET-INITIAL-STATE':
            newState = initialState;
            break;

        case 'SET-INITIAL-STATE-WITH-CENTER':
            newState = initialState;
            newState.centerId = action.centerId;
            break;
    }

    return newState;
}

const AddData = (props: IProps) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { validation, validate, reset, isValid } = useValidation();

    const [dirty, setDirty] = useState(false);

    const previousShow = usePrevious(props.show);

    const handleFieldChange = (e: FormEvent<FormControl & (HTMLInputElement | HTMLSelectElement)>) => {
        dispatch({ type: 'CHANGE-FIELD', property: e.currentTarget.id, value: e.currentTarget.value });
    };

    const handleSave = () => {
        validate(state);

        setDirty(true);
    };

    useEffect(() => {
        if (previousShow == false && props.show) {
            dispatch({ type: 'SET-INITIAL-STATE-WITH-CENTER', centerId: props.showCenter ? '0' : props.centerId.toString() });
            reset();
            setDirty(false);
        } else {
            if (dirty) {
                if (isValid()) {
                    setDirty(false);

                    let { centerId, dataDate, ddTxps, ldTxps, wlTotal, wlActive, lda, ldb, ldab, ldo, ldra, ldrb, ldrab, ldro } = state;

                    props.onSave(
                        centerId, moment(dataDate, 'MM-YYYY').format('MM-DD-YYYY'),
                        ddTxps, ldTxps, wlTotal, wlActive,
                        lda, ldb, ldab, ldo,
                        ldra, ldrb, ldrab, ldro
                    );
                }
            }
        }
    }, [dirty, props.show, validation]);

    return (
        <Modal backdrop='static' show={props.show} onHide={props.onCancel}>
            <Modal.Header closeButton>Add Monthly UNOS Data</Modal.Header>

            <Modal.Body>
                {dirty && !isValid() && (
                    <Alert bsStyle='danger' bsSize='sm'>
                        The red items below must be specified...
                    </Alert>
                )}

                <Form horizontal>
                    {props.showCenter && (
                        <FormGroup validationState={dirty && validation.center == false ? 'error' : null}>
                            <Col xs={12} lg={5} componentClass={ControlLabel}>Center:</Col>
                            <Col xs={12} lg={7}>
                                <FormControl
                                    id='centerId'
                                    componentClass='select'
                                    value={state.centerId}
                                    onChange={handleFieldChange}
                                >
                                    <option value={0}>- Select Center -</option>
                                    {props.centers.map(center =>
                                        <option key={'ctr-' + center.id} value={center.id}>{center.alias}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>
                    )}

                    <FormGroup validationState={dirty && validation.dataDate == false ? 'error' : null}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>Month/Year: *</Col>
                        <Col xs={12} lg={7}>
                            <MaskedInput
                                id='dataDate'
                                className='form-control'
                                mask='11-1111'
                                placeholder='MM-YYYY'
                                style={{ borderColor: validation.dataDate ? '#ccc' : '#a94442' }}
                                value={state.dataDate}
                                onChange={handleFieldChange}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup validationState={dirty && validation.ddTxps == false ? 'error' : null}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>Deceased Donor Transplants:</Col>
                        <Col xs={12} lg={7}>
                            <FormControl
                                id='ddTxps'
                                value={state.ddTxps}
                                onChange={handleFieldChange}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup validationState={dirty && validation.ldTxps == false ? 'error' : null}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>Living Donor Transplants:</Col>
                        <Col xs={12} lg={7}>
                            <FormControl
                                id='ldTxps'
                                value={state.ldTxps}
                                onChange={handleFieldChange}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup validationState={dirty && validation.wlTotal == false ? 'error' : null}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>Waitlist Total:</Col>
                        <Col xs={12} lg={7}>
                            <FormControl
                                id='wlTotal'
                                value={state.wlTotal}
                                onChange={handleFieldChange}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup validationState={dirty && validation.wlActive == false ? 'error' : null}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>Waitlist Active:</Col>
                        <Col xs={12} lg={7}>
                            <FormControl
                                id='wlActive'
                                value={state.wlActive}
                                onChange={handleFieldChange}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup validationState={dirty && validation.lda == false ? 'error' : null}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>Living Donor ABO A:</Col>
                        <Col xs={12} lg={7}>
                            <FormControl
                                id='lda'
                                value={state.lda}
                                onChange={handleFieldChange}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup validationState={dirty && validation.ldb == false ? 'error' : null}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>Living Donor ABO B:</Col>
                        <Col xs={12} lg={7}>
                            <FormControl
                                id='ldb'
                                value={state.ldb}
                                onChange={handleFieldChange}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup validationState={dirty && validation.ldab == false ? 'error' : null}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>Living Donor ABO AB:</Col>
                        <Col xs={12} lg={7}>
                            <FormControl
                                id='ldab'
                                value={state.ldab}
                                onChange={handleFieldChange}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup validationState={dirty && validation.ldo == false ? 'error' : null}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>Living Donor ABO O:</Col>
                        <Col xs={12} lg={7}>
                            <FormControl
                                id='ldo'
                                value={state.ldo}
                                onChange={handleFieldChange}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup validationState={dirty && validation.ldra == false ? 'error' : null}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>Living Donor Recipient ABO A:</Col>
                        <Col xs={12} lg={7}>
                            <FormControl
                                id='ldra'
                                value={state.ldra}
                                onChange={handleFieldChange}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup validationState={dirty && validation.ldrb == false ? 'error' : null}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>Living Donor Recipient ABO B:</Col>
                        <Col xs={12} lg={7}>
                            <FormControl
                                id='ldrb'
                                value={state.ldrb}
                                onChange={handleFieldChange}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup validationState={dirty && validation.ldrab == false ? 'error' : null}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>Living Donor Recipient ABO AB:</Col>
                        <Col xs={12} lg={7}>
                            <FormControl
                                id='ldrab'
                                value={state.ldrab}
                                onChange={handleFieldChange}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup validationState={dirty && validation.ldro == false ? 'error' : null}>
                        <Col xs={12} lg={5} componentClass={ControlLabel}>Living Donor Recipient ABO O:</Col>
                        <Col xs={12} lg={7}>
                            <FormControl
                                id='ldro'
                                value={state.ldro}
                                onChange={handleFieldChange}
                            />
                        </Col>
                    </FormGroup>
                </Form>
            </Modal.Body>

            <Modal.Footer>
                <Button bsStyle='primary' onClick={handleSave}>Save</Button>
                <Button onClick={props.onCancel}>Cancel</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default AddData;